import { migrateTableColumnPrefs } from "@/lib/utils/tanstack-table-local-storage";
import { DataTableVisibilityDropdown } from "@/components/ui/shadcn/DataTable/DataTableVisibilityDropdown";
import { type tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { cn } from "@/lib/utils/cn";
import { difference, isNil } from "lodash";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  type ColumnDef,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type SortingState,
  type Updater,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { DraggableCardTable } from "./DraggableCardTable";

type ColumnMetaWithClassName = { cellClassName?: string };

/** Matches `DataTable` / matters report table page sizes. */
export const DRAGGABLE_CARD_DATA_TABLE_PAGE_SIZE_OPTIONS = [
  10, 25, 50, 100,
] as const;

export type DraggableCardDataTableProps<TData> = {
  searchBar?: ReactNode;
  /** Shown before the column visibility control (e.g. help / tour). */
  tableToolbarBeforeColumns?: ReactNode;
  tableButtons?: ReactNode;
  columns: ColumnDef<TData>[];
  data: TData[];
  isLoading: boolean;
  isFetching?: boolean;
  isError: boolean;
  onRowClick: (row: Row<TData>) => void;
  renderContextMenu?: (row: Row<TData>, trigger: ReactNode) => ReactNode;
  tableName: (typeof tanstackTableNames)[keyof typeof tanstackTableNames];
  entityLabelPlural?: string;
  allowLocalStorage?: boolean;
  columnIdAliases?: Record<string, string>;
  newColumnAnchors?: Record<string, string>;
  /** Locks column widths and truncates cell content instead of reflowing on new data. */
  fixedColumnWidths?: boolean;
  noResultsMessage?: string;
  /** Used when nothing is stored in localStorage yet. */
  initialSorting?: SortingState;
  initialVisibility?: VisibilityState;
  pageSizeOptions?: readonly number[];
  serverPagination?: {
    pagination: PaginationState;
    onPaginationChange: (updater: Updater<PaginationState>) => void;
    pageCount: number;
    rowCount?: number;
    getCanNextPage?: boolean;
  };
  serverSorting?: {
    sorting: SortingState;
    onSortingChange: (updater: Updater<SortingState>) => void;
  };
  /** Constrains the table body to its parent and enables internal vertical scrolling. */
  scrollableBody?: boolean;
};

export function DraggableCardDataTable<TData>({
  searchBar,
  tableToolbarBeforeColumns,
  tableButtons,
  columns,
  data,
  isLoading,
  isFetching = false,
  isError,
  onRowClick,
  renderContextMenu,
  tableName,
  entityLabelPlural = "rows",
  allowLocalStorage = true,
  columnIdAliases = {},
  newColumnAnchors = {},
  fixedColumnWidths = false,
  noResultsMessage = "No results found.",
  initialSorting = [],
  initialVisibility = {},
  pageSizeOptions = DRAGGABLE_CARD_DATA_TABLE_PAGE_SIZE_OPTIONS,
  serverPagination,
  serverSorting,
  scrollableBody = false,
}: DraggableCardDataTableProps<TData>) {
  const localStorageObject = allowLocalStorage
    ? window.localStorage.getItem(tableName)
    : null;
  type StoredTableState = {
    columnOrder?: string[];
    columnVisibility?: VisibilityState;
    pagination?: { pageSize?: number };
    sorting?: SortingState;
  };

  const localStorageJSON: StoredTableState | undefined = localStorageObject
    ? migrateTableColumnPrefs(
        JSON.parse(localStorageObject) as StoredTableState,
        columnIdAliases,
        newColumnAnchors,
      )
    : undefined;

  const tableCols = useMemo(
    () => columns.map((c) => (c.id as string) || ""),
    [columns],
  );

  const unHidableColumns = useMemo(
    () =>
      columns
        .filter((column) => column.enableHiding === false)
        .reduce<VisibilityState>((acc, column) => {
          return {
            ...acc,
            [column.id as string]: true,
          };
        }, {}),
    [columns],
  );

  const [sorting, setSorting] = useState<SortingState>(
    () => localStorageJSON?.sorting ?? initialSorting,
  );
  const [pagination, setPagination] = useState<PaginationState>(() => {
    const defaultPageSize =
      pageSizeOptions.length > 0 ? pageSizeOptions[0] : 10;
    return {
      pageIndex: 0,
      pageSize: localStorageJSON?.pagination?.pageSize ?? defaultPageSize,
    };
  });
  const tablePagination = serverPagination?.pagination ?? pagination;
  const tableSorting = serverSorting?.sorting ?? sorting;

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () =>
      localStorageJSON?.columnVisibility
        ? {
            ...initialVisibility,
            ...localStorageJSON.columnVisibility,
            ...unHidableColumns,
          }
        : { ...initialVisibility, ...unHidableColumns },
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    localStorageJSON?.columnOrder
      ? [
          ...localStorageJSON.columnOrder,
          ...difference(tableCols, localStorageJSON.columnOrder),
        ]
      : tableCols,
  );

  useEffect(() => {
    setColumnOrder((prev) => {
      const nextIds = columns.map((c) => (c.id as string) || "");
      if (
        prev.length === nextIds.length &&
        prev.every((id, i) => id === nextIds[i])
      ) {
        return prev;
      }
      return [
        ...prev.filter((id) => nextIds.includes(id)),
        ...difference(nextIds, prev),
      ];
    });
  }, [columns]);

  const persistTableState = (patch: Record<string, unknown>) => {
    if (!allowLocalStorage) return;
    let base: Record<string, unknown> = {};
    try {
      const raw = window.localStorage.getItem(tableName);
      if (raw) {
        base = JSON.parse(raw) as Record<string, unknown>;
      }
    } catch {
      base = {};
    }
    window.localStorage.setItem(
      tableName,
      JSON.stringify({ ...base, ...patch }),
    );
  };

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = functionalUpdate(updater, tableSorting);
    persistTableState({ sorting: next });
    if (serverSorting) {
      serverSorting.onSortingChange(next);
    } else {
      setSorting(next);
    }
    const resetPagination = { ...tablePagination, pageIndex: 0 };
    if (serverPagination) {
      serverPagination.onPaginationChange(resetPagination);
    } else {
      setPagination(resetPagination);
    }
  };

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    const next = functionalUpdate(updater, tablePagination);
    persistTableState({
      pagination: { pageSize: next.pageSize },
    });
    if (serverPagination) {
      serverPagination.onPaginationChange(updater);
    } else {
      setPagination(updater);
    }
  };

  const handleColumnOrderChange = (updater: Updater<string[]>) => {
    const next = functionalUpdate(updater, columnOrder);
    persistTableState({ columnOrder: next });
    setColumnOrder(updater);
  };

  const handleColumnVisibilityChange = (updater: Updater<VisibilityState>) => {
    const next = functionalUpdate(updater, columnVisibility);
    persistTableState({ columnVisibility: next });
    setColumnVisibility(updater);
  };

  const table = useReactTable({
    data,
    columns,
    autoResetPageIndex: serverPagination ? false : true,
    manualPagination: Boolean(serverPagination),
    manualSorting: Boolean(serverSorting),
    pageCount: serverPagination?.pageCount,
    rowCount: serverPagination?.rowCount,
    state: {
      sorting: tableSorting,
      columnOrder,
      columnVisibility,
      pagination: tablePagination,
    },
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: {
      customDateTime: (rowA, rowB, columnId) => {
        if (isNil(rowA.getValue(columnId))) {
          return 1;
        }
        if (isNil(rowB.getValue(columnId))) {
          return -1;
        }
        return dayjs(rowA.getValue(columnId)).diff(
          dayjs(rowB.getValue(columnId)),
        );
      },
    },
  });

  const toolbarTrailing = (
    <>
      {tableToolbarBeforeColumns}
      <DataTableVisibilityDropdown table={table} />
      {tableButtons}
    </>
  );

  return (
    <div
      className={cn(
        "border-border bg-card w-full min-w-0 overflow-hidden rounded-xl border shadow-xs",
        scrollableBody && "flex max-h-full min-h-0 flex-col",
      )}
    >
      <DraggableCardTable
        table={table}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        emptyMessage={noResultsMessage}
        toolbarLeading={searchBar}
        toolbarTrailing={toolbarTrailing}
        onRowClick={onRowClick}
        renderContextMenu={renderContextMenu}
        getCellClassName={(cell) =>
          (cell.column.columnDef.meta as ColumnMetaWithClassName | undefined)
            ?.cellClassName
        }
        entityLabelPlural={entityLabelPlural}
        pageSizeOptions={pageSizeOptions}
        isServerPaginated={Boolean(serverPagination)}
        serverRowCount={serverPagination?.rowCount}
        serverCanNextPage={serverPagination?.getCanNextPage}
        fixedColumnWidths={fixedColumnWidths}
        scrollRestoreSorting={tableSorting}
        scrollRestoreColumnOrder={columnOrder}
        scrollableBody={scrollableBody}
      />
    </div>
  );
}
